<template>
  <ion-page id="main-content">
    <ion-header>
      <ion-toolbar>
        <!-- Links: wechselnde Navigation, scrollt bei Platzmangel waagerecht.
             Rechts: feste Buttons, bleiben immer sichtbar. -->
        <ion-buttons
          slot="start"
          class="nav-main"
        >
          <top-navigation-main-buttons />
        </ion-buttons>
        <ion-buttons slot="end">
          <cloud-status />
        </ion-buttons>
        <ion-buttons slot="end">
          <top-navigation-additional-buttons />
        </ion-buttons>
        <ion-buttons slot="end">
          <user-avatar
            @click="navigateToProfile"
          />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <!-- ion-content scrollt nicht selbst: der feste Rahmen (.page-frame) hält Abstand,
         darin scrollt .page-body bzw. bei `fill` die Insel (.ion-content-scroll-host). -->
    <ion-content
      color="tertiary"
      :scroll-y="false"
    >
      <ion-refresher
        slot="fixed"
        @ion-refresh="handleRefresh($event)"
      >
        <ion-refresher-content />
      </ion-refresher>
      <!-- FABs (slot="fixed") müssen direkte Kinder von ion-content sein -->
      <slot name="fixed" />
      <div
        class="page-frame"
        :class="{'page-frame--full': fullWidth}"
        @scroll.capture="handleScroll"
      >
        <!-- Fester Kopfbereich (z. B. Tabs): scrollt nicht mit -->
        <div
          v-if="$slots.top"
          class="page-top"
        >
          <slot name="top" />
        </div>
        <div
          class="page-body"
          :class="{'ion-content-scroll-host': !fill, 'page-body--fill': fill}"
        >
          <slot />
        </div>
      </div>
    </ion-content>
    <save-button
      v-if="hasSave"
      @save="save"
    />
    <scroll-button
      v-if="showScrollButton"
      @click="scrollToTop"
    />
  </ion-page>
</template>

<script>
import {
    IonPage,
    IonHeader,
    IonContent,
    IonToolbar,
    IonButtons,
    IonRefresher,
    IonRefresherContent,
} from '@ionic/vue';
import { homeSharp, arrowUpOutline } from 'ionicons/icons';
import CloudStatus from './CloudStatus.vue';
import SaveButton from './SaveButton.vue';
import ScrollButton from './ScrollButton.vue';
import TopNavigationMainButtons from './TopNavigationMainButtons.vue';
import TopNavigationAdditionalButtons from './TopNavigationAdditionalButtons.vue';
import UserAvatar from './UserAvatar.vue';

export default {
    components: {
        CloudStatus,
        SaveButton,
        TopNavigationMainButtons,
        TopNavigationAdditionalButtons,
        IonPage,
        IonHeader,
        IonContent,
        IonToolbar,
        IonButtons,
        IonRefresher,
        IonRefresherContent,
        ScrollButton,
        UserAvatar,
    },
    props: {
        pageTitle: {
            type: String,
            required: false,
            default: ''
        },
        pageDefaultBackLink: {
            type: String,
            required: false,
            default: ''
        },
        isHome: {
            type: Boolean,
            required: false
        },
        hasSave: {
            type: Boolean,
            required: false
        },
        /** Seite scrollt nicht selbst; eine <page-island fill> füllt die Höhe und scrollt intern. */
        fill: {
            type: Boolean,
            required: false
        },
        /** Ohne max-width und Seitenrand (ManagementPage bringt ein eigenes Raster mit). */
        fullWidth: {
            type: Boolean,
            required: false
        },
    },
    emits: ['save'],
    data() {
        return {
            homeSharp,
            arrowUpOutline,
            showScrollButton: false,
        };
    },
    computed: {
        usageAllowed() {
            return this.$store.getters.usageAllowed;
        },
    },
    created() {
        // Scroll-Host, der zuletzt gescrollt hat (Seite oder fill-Insel). Bewusst nicht
        // reaktiv: wird bei jedem Scroll-Event gesetzt, das Template liest es nicht.
        this.scrollEl = null;
    },
    mounted() {
        this.$store.dispatch('loadImage', { fileName: 'avatar.png' });
    },
    methods: {
        save() {
            this.$emit('save');
        },
        goHome() {
            this.$router.push('/');
        },
        handleRefresh(event) {
            this.$store.dispatch('downsync');
            this.$syncCallbacks.executeCallbacks();
            setTimeout(() => {
                // Any calls to load data go here
                event.target.complete();
            }, 1000);
        },
        /**
         * Scroll-Events bubbeln nicht, werden aber im Capture-Modus abgefangen - so
         * zählt auch eine scrollende Insel. Nur der Scroll-Host der Seite ist gemeint,
         * nicht z. B. horizontal scrollende Tabellen.
         * @param {Event} scrollEvent - scroll event of the frame or a descendant
         */
        handleScroll(scrollEvent) {
            const target = scrollEvent.target;
            if (!target.classList.contains('ion-content-scroll-host')) {
                return;
            }
            this.scrollEl = target;
            this.showScrollButton = target.scrollTop > window.innerHeight * 2;
        },
        scrollToTop() {
            if (this.scrollEl) {
                this.scrollEl.scrollTo({top: 0, behavior: 'smooth'});
            }
        },
        navigateToProfile() {
            this.$router.push('/profile');
        },
    },
};
</script>

<style scoped>
/* Die linke Navigation darf schrumpfen und scrollt dann (Wischen auf dem iPad);
   die rechten Buttons schrumpfen nie. */
.nav-main {
  flex: 0 1 auto;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.nav-main::-webkit-scrollbar {
  display: none;
}

.nav-main :deep(ion-button) {
  flex-shrink: 0;
}

ion-buttons[slot="end"] {
  flex-shrink: 0;
}
</style>
